/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 1 hook(s) need dependency review; fix each and remove this directive */
import { useTranslation } from "@i18n";
import { useEffect, useRef, useState, useMemo } from "react";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import { formUsersUrl } from "@Services/dataManager";
import UserNameTagComponent from "@CubeComponents/html/UserNameTagComponent";
import { renderUserTag } from "@CubeComponents/html/userTagDom";
import { useDispatch } from "react-redux";
import { showToastNotification } from "@Reducers/toastSlice";
import { TOAST_TYPE_WARNING } from "@Services/constants";
import SlickGrid1 from "@CubeComponents/html/SlickGrid1";
import { typedColumns, type GridRowBase } from "@CubeComponents/html/SlickGrid/types";
/** Row type for user data */
interface UserRow extends GridRowBase {
  fullName?: string;
  email?: string;
  organisation?: { name?: string };
  designation?: string;
}

type User = {
  id: number;
  fullName: string;
};

const AddUser = ({
  isVisible = true,
  closeUsersModal,
  handleAddUserClick,
  currentUsers = [],
  section,
  fieldData,
  spaceId,
  formId,
  projectId,
  customQuery,
  handleCustomFieldData,
}: any) => {
  const { t } = useTranslation();
  const [disableAddBtn, setDisableAddBtn] = useState(true);
  const dispatch = useDispatch();
  const [allSelectedUsers, setAllSelectedUsers] = useState<User[]>([]);
  const selectionMode = fieldData?.selectionType || "multipleUser";
  const dataGrid: any = useRef<any>(null);

  const headerContent = () => (
    <div className=" w-full flex justify-between rounded-md">
      <div className="">{t("Add Users")}</div>
    </div>
  );

  useEffect(() => {
    const data = currentUsers.map((user) => ({ id: user.id, fullName: user.name }));
    setAllSelectedUsers(data);
  }, []);

  const footerContent = () => (
    <div className=" flex gap-4">
      <ButtonComponent
        disabled={disableAddBtn}
        buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
        onClick={() => {
          handleAddUserClick({
            handleCustomFieldData,
            preSelectedUsers: allSelectedUsers, // patch
            allSelectedUsers,
          });
          closeUsersModal();
        }}
      >
        {t("Add")}
      </ButtonComponent>
      <ButtonComponent buttonType={BUTTON_TYPES.BACKGROUND_BUTTON} onClick={closeUsersModal}>
        {t("Cancel")}
      </ButtonComponent>
    </div>
  );

  useEffect(() => {
    setDisableAddBtn(allSelectedUsers.length == 0);
  }, [allSelectedUsers]);

  const handleRemoveUser = (userId: any) => {
    setAllSelectedUsers((prev: any) => prev.filter((user: any) => user.id != userId));
  };

  const nameTemplate = (
    _row: number,
    _cell: number,
    _value: string | undefined,
    _columnDef: unknown,
    dataContext: UserRow,
  ) => {
    const { id } = dataContext;
    const container = document.createElement("div");
    renderUserTag(container, { id: id });
    return container;
  };

  const handleAddUserToSelection = () => {
    const selectedUsers = dataGrid.current?.dataView?.getAllSelectedItems?.() || [];
    let data = [...allSelectedUsers, ...selectedUsers];
    let uniqueData = [...new Map(data.map((item: any) => [item.id, item])).values()];
    if (selectionMode == "singleUser" && uniqueData.length > 1) {
      uniqueData = uniqueData.length > 0 ? [uniqueData[0]] : [];
      dispatch(
        showToastNotification({
          content: t("Only single user can be added"),
          type: TOAST_TYPE_WARNING,
        }),
      );
    }
    setAllSelectedUsers(() => uniqueData);
  };

  const queryObj: any = customQuery ?? {
    projectId,
    sectionId: section?._id,
    selectionScope: fieldData?.selectionScope,
    formId,
    params: {
      projectId,
      sectionId: section?._id,
      selectionScope: fieldData?.selectionScope,
    },
  };
  if (spaceId) {
    queryObj.spaceId = spaceId;
    queryObj.params.spaceId = spaceId;
  }

  const [query] = useState<any>(queryObj);
  const columnDefinitions = useMemo(
    () =>
      typedColumns<UserRow>([
        {
          field: "fullName",
          id: "fullName",
          filterType: "Text",
          name: t("User Name"),
          width: 150,
          formatter: nameTemplate,
        },
        {
          field: "email",
          id: "email",
          filterType: "Text",
          name: t("E-Mail ID"),
          width: 150,
        },
        {
          field: "organisation",
          id: "organisation.name",
          filterType: "Text",
          name: t("Org"),
          width: 150,
          _valueGetter: (row) => row.organisation?.name,
        },
        {
          field: "designation",
          id: "designation",
          filterType: "Text",
          name: t("Designation"),
          width: 150,
        },
      ]),
    [],
  );
  const getUsersGrid = () => (
    <SlickGrid1
      ref={dataGrid}
      remoteDataSource={formUsersUrl}
      query={query}
      infiniteScroll
      enableFilteringOption
      disabledIds={currentUsers.map((u: any) => u.id)}
      enableSortingOption
      columnDefinitions={columnDefinitions as any}
    />
  );
  const modelContent = () => (
    <div className="w-full h-full overflow-auto gap-5 flex flex-col">
      <div className="flex flex-2 flex-col overflow-auto border border-button_light rounded-lg">
        <div className="border border-button_light" />
        {getUsersGrid()}
        <div className="p-4">
          <ButtonComponent onClick={handleAddUserToSelection}>{t("Add to selection")}</ButtonComponent>
        </div>
      </div>
      <div className="border flex-1 overflow-auto flex flex-col border-button_light rounded-lg">
        <div className="p-4 font-semibold">{t("Selection")}</div>
        <div className="border border-button_light" />
        <div className="flex p-4 overflow-auto flex-wrap gap-3">
          {allSelectedUsers.map((user: any) => (
            <UserNameTagComponent
              key={user.id}
              handleDeleteIconClicked={() => {
                handleRemoveUser(user.id);
              }}
              id={user.id}
            />
          ))}
        </div>
      </div>
    </div>
  );

  const getUserModal = () => (
    <ModalStateDialogComponent
      allowModalPadding={false}
      footerContent={footerContent()}
      width="70%"
      height="100%"
      headerTitle={headerContent()}
      onCancel={closeUsersModal}
      isVisible={isVisible}
      modelContent={modelContent()}
    />
  );

  return getUserModal();
};
export default AddUser;
